/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { isDevelopmentEnvironment, LuisDevEnvironments, UnifiedAuthoringDevEnvironments } from 'src/core/utils/api/environment';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { supportedLocales } from 'src/core/utils/supportedLocales';

const messages = defineMessages({
    languageLabel: {
        id: 'languageStudio.generalSettings.languageLabel',
        defaultMessage: 'Choose the language displayed in the portal'
    },
    currentResourceLabel: {
        id: 'languageStudio.generalSettings.currentResourceLabel',
        defaultMessage: 'Current Language Resource:'
    },
    storageTitle: {
        id: 'languageStudio.generalSettings.storageTitle',
        defaultMessage: 'Storage'
    },
    storageDescription: {
        id: 'languageStudio.generalSettings.storageDescription',
        defaultMessage: 'Your storage account is where your documents are located. One storage account is tied to one resource.'
    },
    storageTypeLabel: {
        id: 'languageStudio.generalSettings.storageTypeLabel',
        defaultMessage: 'Storage type:'
    },
    storageAccountNameLabel: {
        id: 'languageStudio.generalSettings.storageAccountNameLabel',
        defaultMessage: 'Storage account name:'
    },
    searchTitle: {
        id: 'languageStudio.generalSettings.searchTitle',
        defaultMessage: 'Search'
    },
    searchDescription: {
        id: 'languageStudio.generalSettings.searchDescription',
        defaultMessage: 'Your Azure Search account is the index for your question answering projects.'
    },
    searchNameLabel: {
        id: 'languageStudio.generalSettings.searchNameLabel',
        defaultMessage: 'Search name:'
    },
    noSearchMessage: {
        id: 'languageStudio.generalSettings.noSearchMessage',
        defaultMessage: 'You do not have any Azure Search accounts connected to your current resource'
    },
    noStorageMessage: {
        id: 'languageStudio.generalSettings.noStorageMessage',
        defaultMessage: 'You do not have any Storage accounts connected to your current resource'
    }
});

const { styleDiv } = getStylist('GeneralSettings');
const labelStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { display: 'table-cell' } });
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { flex: 1, minHeight: 0 } });
const rowStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { display: 'table-row', lineHeight: '30px' } });
const valueStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { display: 'table-cell', paddingLeft: '30px' }
});
const searchValueStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { display: 'table-cell', paddingLeft: '90px' }
});

const InfoRow = styleDiv('InfoRow', { marginTop: '11px' });

const { setData, getData } = getStorage('localStorage');
const isProductionDevEnvEnabled = tryParseJson<boolean>(getData(LuisConstants.StorageKeys.ProductionDevEnvironmentEnabled)) ?? false;

const devSettings = () => (
    <Stack tokens={{ childrenGap: 15 }}>
        <LuisText variant="large">Dev Settings</LuisText>

        <Toggle
            onText="Disable production mode"
            offText="Enable production mode"
            checked={isProductionDevEnvEnabled}
            onChange={(_, checked) => {
                setData(LuisConstants.StorageKeys.ProductionDevEnvironmentEnabled, `${checked}`);
                location.reload();
            }}
        />

        <LuisDropdown
            label="LUIS Dev Environment"
            placeholder="Choose LUIS development environment ..."
            selectedKey={getData(LuisConstants.StorageKeys.LuisDevBaseUrlStorageKey)}
            options={[
                { key: LuisDevEnvironments.Dialogice3, text: 'Dialogice3', data: LuisDevEnvironments.Dialogice3 },
                { key: LuisDevEnvironments.Dialogice4, text: 'Dialogice4', data: LuisDevEnvironments.Dialogice4 },
                { key: LuisDevEnvironments.Dialogice6, text: 'Dialogice6', data: LuisDevEnvironments.Dialogice6 },
                { key: LuisDevEnvironments.Dialogice14, text: 'Dialogice14', data: LuisDevEnvironments.Dialogice14 }
            ]}
            onChange={(_, option) => {
                setData(LuisConstants.StorageKeys.LuisDevBaseUrlStorageKey, option.data as string);
                location.reload();
            }}
        />

        <LuisDropdown
            label="Unified Authoring Dev Environment"
            placeholder="Choose Unified Authoring development environment ..."
            selectedKey={getData(LuisConstants.StorageKeys.UnifiedAuthoringBaseUrlStorageKey)}
            options={[
                {
                    text: 'With Love Central',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringWithLoveCentral,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringWithLoveCentral
                },
                {
                    text: 'With Love East',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringWithLoveEast,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringWithLoveEast
                },
                {
                    text: 'Nightly',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringNightly,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringNightly
                },
                {
                    text: 'Stable',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringStable,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringStable
                },
                {
                    text: 'PPE',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringPpe,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringPpe
                },
                {
                    text: 'PPE APIM (TIP)',
                    key: UnifiedAuthoringDevEnvironments.UnifiedAuthoringPpeApim,
                    data: UnifiedAuthoringDevEnvironments.UnifiedAuthoringPpeApim
                }
            ]}
            onChange={(_, option) => {
                setData(LuisConstants.StorageKeys.UnifiedAuthoringBaseUrlStorageKey, option.data as string);
                location.reload();
            }}
        />
    </Stack>
);

export const GeneralSettings = observer(() => {
    const { localize } = useLocalization();

    const { dispatcher, rootStore, uiStore } = useGlobalState();

    const selectedLocale = uiStore.locale;
    const azureSession = rootStore.azureStore.session;
    const currentResource = azureSession.csAccountStore;

    const onLocaleChange = (code: Locale['code']) => {
        dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: null });
        dispatcher.dispatch('setCurrentLocale', { locale: supportedLocales.find(l => l.code === code) });
    };

    return (
        <Stack styles={containerStyles} tokens={{ childrenGap: 18 }}>
            {isDevelopmentEnvironment() && devSettings()}

            <LuisDropdown
                isSearchEnabled={false}
                selectedKey={selectedLocale.code}
                placeholder={selectedLocale.name}
                styles={{ root: { maxWidth: 512 } }}
                label={localize(messages.languageLabel)}
                onChange={(_, option) => onLocaleChange(option.key as Locale['code'])}
                options={supportedLocales.map(locale => ({ key: locale.code, text: locale.name }))}
            />

            {currentResource && (
                <LuisText>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.currentResourceLabel)}
                    </LuisText>
                    &nbsp;
                    <LuisText variant="mediumPlus">{currentResource.name}</LuisText>
                </LuisText>
            )}

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" fontWeight={500}>
                    {localize(messages.storageTitle)}
                </LuisText>
                <LuisText>{localize(currentResource?.ownedStorageId ? messages.storageDescription : messages.noStorageMessage)}</LuisText>
            </Stack>

            {currentResource?.ownedStorageId && (
                <InfoRow>
                    <Stack horizontal styles={rowStyles}>
                        <LuisText styles={labelStyles} fontWeight={600}>
                            {localize(messages.storageTypeLabel)}
                        </LuisText>
                        <LuisText styles={valueStyles}>Azure Blob storage</LuisText>
                    </Stack>
                    <Stack horizontal styles={rowStyles}>
                        <LuisText fontWeight={600} styles={labelStyles}>
                            {localize(messages.storageAccountNameLabel)}
                        </LuisText>
                        <LuisText styles={valueStyles}>{currentResource.ownedStorageAccountInfo.name}</LuisText>
                    </Stack>
                </InfoRow>
            )}

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText variant="mediumPlus" fontWeight={500}>
                    {localize(messages.searchTitle)}
                </LuisText>
                <LuisText>
                    {localize(currentResource?.ownedSearchResourceId ? messages.searchDescription : messages.noSearchMessage)}
                </LuisText>
            </Stack>

            {currentResource?.ownedSearchResourceId && (
                <InfoRow>
                    <Stack horizontal styles={rowStyles}>
                        <LuisText styles={labelStyles} fontWeight={600}>
                            {localize(messages.searchNameLabel)}
                        </LuisText>
                        <LuisText styles={searchValueStyles}>{currentResource.ownedSearchResourceInfo.name}</LuisText>
                    </Stack>
                </InfoRow>
            )}
        </Stack>
    );
});
